import ActionButton from 'components/common/ActionButton';
import FalconComponentCard from 'components/common/FalconComponentCard';
import PageHeader from 'components/common/PageHeader';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import corner3 from 'assets/img/illustrations/corner-3.png';
import api from 'util/api';
import enums from 'util/enums';
import _ from 'lodash';
import logger from 'util/logger';
import { showAlert, showConfirm } from 'stores/modal';
import moment from 'moment';
import Flex from 'components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SimpleBarReact from 'simplebar-react';
import Colors from 'util/Colors';
import { useDispatch } from 'react-redux';
import { DeviceIcon } from 'data/common/common';
import { useListDevicesQuery } from 'rtk/device.slice';
import Pagination from 'components/common/Pagination';

const DevicesList = () => {
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const dispatch = useDispatch();

  const {
    data: devicesRes,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useListDevicesQuery({
    skip: pageIndex * pageSize,
    pageSize: pageSize
  });
  const loadDevices = () => {
    refetch();
  };
  const devices = devicesRes?.Objects || [];
  const totalRecords = devicesRes?.TotalRecords || 0;
  const endOfResults = devicesRes?.EndOfResults || false;
  const devicesLoadError = isError || false;
  const loading = isLoading || isFetching;

  const deleteDevice = device => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to remove this device?',
        res => {
          if (res) {
            api.Devices.delete(device.GUID)
              .then(() => {
                loadDevices();
              })
              .catch(() => {
                showAlert(
                  'Error Deleting Device',
                  'There was a problem removing this device.  Please try again.'
                );
                loadDevices();
              });
          }
        }
      )
    );
  };

  const blockDevice = device => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to block this device?  Blocking a device will prevent anyone from using it to sign in via the mobile app.',
        res => {
          if (res) {
            api.Devices.block(device.GUID)
              .then(res => {
                logger.log('block device res', res);
                loadDevices();
              })
              .catch(() => {
                showAlert(
                  'Error Blocking Device',
                  'There was a problem blocking this device.  Please try again.'
                );
                loadDevices();
              });
          }
        }
      )
    );
  };

  const unblockDevice = device => {
    api.Devices.unblock(device.GUID)
      .then(res => {
        logger.log('unblock device res', res);
        loadDevices();
      })
      .catch(() => {
        dispatch(
          showAlert(
            'Error Unblocking Device',
            'There was a problem unblocking this device.  Please try again.'
          )
        );
        loadDevices();
      });
  };

  const DevicePopover = e => {
    return (
      <Popover id="device-popover">
        <Popover.Header as="h3" className="d-inline-block">
          <FontAwesomeIcon
            size="sm"
            color={e.Blocked ? Colors.warning.color : Colors.success.color}
            icon="circle"
          />{' '}
          <span className="text-truncate">
            {e.Blocked ? 'Blocked' : 'Active'} Device
          </span>
        </Popover.Header>
        <Popover.Body className="p-0">
          <Table bordered responsive className="fs--1">
            <tbody>
              <tr>
                <td scope="col">Device</td>
                <td scope="col">
                  {e.Manufacturer} {e.Model}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="fs--2">
                  {e.Identifier}
                </td>
              </tr>
              <tr>
                <td>Version</td>
                <td>{e.AppVersion}</td>
              </tr>
              <tr>
                <td>IP</td>
                <td>{e.LastIpAddress}</td>
              </tr>
            </tbody>
          </Table>
        </Popover.Body>
      </Popover>
    );
  };

  useEffect(() => {
    setPageIndex(0);
    loadDevices(0);
  }, [pageSize]);

  return (
    <>
      {/* <PageHeader
        preTitle="Active Devices"
        title="Devices"
        description="You have successfully logged in to The Notary eJournal from these devices. You can block or remove devices at any time, giving you more control over the security of your account"
        className="mb-3"
        titleTag="h2"
        image={corner3}
      /> */}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              title="Devices"
              subtitle="You have successfully logged in to The Notary eJournal from these devices. You can block or remove devices at any time, giving you more control over the security of your account"
              image={corner3}
            />
            <FalconComponentCard.Body noLight className="py-0">
              {isLoading && devices.length > 0 && (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  className="gap-2 py-2 border-bottom border-200 bg-light"
                >
                  <Spinner size="sm" />
                  <span className="text-muted fs--1">Loading devices…</span>
                </Flex>
              )}
              {isLoading && devices.length === 0 && (
                <Flex justifyContent="center" className="py-5">
                  <Spinner size="sm" />
                </Flex>
              )}
              {!loading && devices.length === 0 && !devicesLoadError && (
                <p className="text-center text-muted py-4 mb-0">
                  No devices found.
                </p>
              )}
              {devicesLoadError && !loading && (
                <p className="text-center text-danger py-4 mb-0">
                  Unable to load devices. Please try again.
                </p>
              )}
              {(devices.length > 0 || pageIndex > 0 || totalRecords > 0) &&
                !devicesLoadError &&
                !isLoading && (
                  <Pagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    currentCount={devices.length}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    endOfResults={endOfResults}
                    disabled={loading}
                    onRefresh={loadDevices}
                    loading={loading}
                  />
                )}
              {devices.length > 0 && (
                <SimpleBarReact>
                  <Table
                    bordered
                    striped
                    className={loading ? 'refreshing-skeleton' : undefined}
                  >
                    <colgroup>
                      <col />
                      <col />
                      <col className="d-sm-none d-md-block" />
                      <col className="d-sm-none d-md-block" />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">Manufacturer</th>
                        <th scope="col">Model</th>
                        <th scope="col">Created</th>
                        <th scope="col">Last used</th>
                        <th className="text-end" scope="col">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.map(devices, e => {
                        let createdDate = moment
                          .utc(e.Created)
                          .local()
                          .format('l, LT');
                        let lastUsedDate = moment
                          .utc(e.LastSeen)
                          .local()
                          .format('l, LT');

                        return (
                          <tr key={e.GUID}>
                            <td>
                              {DeviceIcon(e)}{' '}
                              <OverlayTrigger
                                trigger="click"
                                placement="top"
                                rootClose
                                overlay={DevicePopover(e)}
                              >
                                <span>{e.Manufacturer}</span>
                              </OverlayTrigger>{' '}
                            </td>
                            <td>{e.Model}</td>
                            <td>{createdDate}</td>
                            <td>{lastUsedDate}</td>
                            <td className="text-end">
                              {e.Blocked && (
                                <ActionButton
                                  icon="check-circle"
                                  title="Unblock"
                                  label="Unblock"
                                  variant="action"
                                  className="p-0 me-3 text-primary"
                                  onClick={() => unblockDevice(e)}
                                />
                              )}
                              {!e.Blocked && (
                                <ActionButton
                                  icon="ban"
                                  title="Block"
                                  label="Block"
                                  variant="action"
                                  className="p-0 me-3 text-warning"
                                  onClick={() => blockDevice(e)}
                                />
                              )}
                              <ActionButton
                                icon="trash-alt"
                                title="Remove"
                                label="Remove"
                                variant="action"
                                className="p-0 text-danger"
                                onClick={() => deleteDevice(e)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </SimpleBarReact>
              )}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
    </>
  );
};

export default DevicesList;
